import React, { useState, useEffect } from 'react';
import { 
  Image as ImageIcon, BarChart3, Video, Edit2, Save, X, Loader2, Search, 
  Shirt, Plus, Sparkles, Zap, Package, Palette
} from 'lucide-react';
import { API_URL } from '../../config';

export default function MarketingQuotas() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({
    max_images_studiopro: 0,
    max_videos_wan: 0,
    max_videos_hailua: 0,
    max_images_universal: 0,
    max_videos_universal: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null); // 'vtryon' ou 'universal'

  useEffect(() => {
    loadClients();
    const interval = setInterval(loadClients, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/clients-quotas`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (client) => {
    setEditingClient(client.$id);
    setEditForm({
      max_images_studiopro: client.max_images_studiopro || 0,
      max_videos_wan: client.max_videos_wan || 0,
      max_videos_hailua: client.max_videos_hailua || 0,
      max_images_universal: client.max_images_universal || 0,
      max_videos_universal: client.max_videos_universal || 0
    });
  };

  const cancelEdit = () => {
    setEditingClient(null);
  };

  const saveQuotas = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/clients/${clientId}/quotas`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        alert('✅ Quotas mis à jour');
        cancelEdit();
        loadClients();
      } else {
        alert('❌ Erreur');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resetQuotas = async (clientId, type, typeName) => {
    if (!window.confirm(`⚠️ Réinitialiser ${typeName} ?\n\nCela va archiver les éléments et mettre les quotas à 0.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/clients/${clientId}/quotas/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type })
      });
      
      if (response.ok) {
        await loadClients();
        const data = await response.json();
        alert(`✅ ${data.reset_count} éléments archivés`);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filteredClients = clients.filter(client =>
    client.facebook_page_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasVTryonQuotas = (client) => {
    return client.max_images_studiopro > 0 || 
           client.max_videos_wan > 0 || 
           client.max_videos_hailua > 0;
  };

  const hasUniversalQuotas = (client) => {
    return client.max_images_universal > 0 || 
           client.max_videos_universal > 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Marketing Visuel - Gestion Quotas</h1>
        <div className="flex gap-3">
          <button
            onClick={loadClients}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:scale-105 transition-all flex items-center gap-2"
          >
            <BarChart3 className="w-5 h-5" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher une page..."
          className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
        />
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6">
          <Shirt className="w-10 h-10 text-white mb-3" />
          <p className="text-white/80 text-sm">Pages Total</p>
          <p className="text-3xl font-bold text-white">{filteredClients.length}</p>
        </div>

        <div className="bg-gradient-to-br from-pink-600 to-rose-600 rounded-2xl p-6">
          <ImageIcon className="w-10 h-10 text-white mb-3" />
          <p className="text-white/80 text-sm">Images VTry-on</p>
          <p className="text-3xl font-bold text-white">
            {clients.reduce((sum, c) => sum + (c.used_images_studiopro || 0), 0)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl p-6">
          <Video className="w-10 h-10 text-white mb-3" />
          <p className="text-white/80 text-sm">Vidéos V12</p>
          <p className="text-3xl font-bold text-white">
            {clients.reduce((sum, c) => sum + (c.used_videos_wan || 0), 0)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl p-6">
          <Palette className="w-10 h-10 text-white mb-3" />
          <p className="text-white/80 text-sm">Images Universal</p>
          <p className="text-3xl font-bold text-white">
            {clients.reduce((sum, c) => sum + (c.used_images_universal || 0), 0)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6">
          <Video className="w-10 h-10 text-white mb-3" />
          <p className="text-white/80 text-sm">Vidéos Universal</p>
          <p className="text-3xl font-bold text-white">
            {clients.reduce((sum, c) => sum + (c.used_videos_universal || 0), 0)}
          </p>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="space-y-4">
        {filteredClients.map(client => {
          const isEditing = editingClient === client.$id;
          const hasVTryon = hasVTryonQuotas(client);
          const hasUniversal = hasUniversalQuotas(client);

          return (
            <div
              key={client.$id}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all"
            >
              {/* En-tête */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">{client.facebook_page_name}</h3>
                <div className="flex gap-2">
                  {hasVTryon && (
                    <div className="px-3 py-1 rounded-full bg-pink-500/20 text-pink-300 text-xs font-bold flex items-center gap-1">
                      <Shirt className="w-3 h-3" />
                      VTry-On
                    </div>
                  )}
                  {hasUniversal && (
                    <div className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-bold flex items-center gap-1">
                      <Palette className="w-3 h-3" />
                      Universal
                    </div>
                  )}
                </div>
              </div>

              {isEditing ? (
                /* MODE ÉDITION */
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Virtual Try-On */}
                    <div className="space-y-3 p-4 bg-pink-500/10 rounded-xl border border-pink-500/30">
                      <h4 className="text-sm font-bold text-pink-300 flex items-center gap-2">
                        <Shirt className="w-4 h-4" />
                        Virtual Try-On (Studio Pro)
                      </h4>
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_images_studiopro}
                        onChange={(e) => setEditForm({...editForm, max_images_studiopro: parseInt(e.target.value) || 0})}
                        placeholder="Max Photos"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500"
                      />
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_videos_wan}
                        onChange={(e) => setEditForm({...editForm, max_videos_wan: parseInt(e.target.value) || 0})}
                        placeholder="Max Vidéos V12"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500"
                      />
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_videos_hailua}
                        onChange={(e) => setEditForm({...editForm, max_videos_hailua: parseInt(e.target.value) || 0})}
                        placeholder="Max Vidéos V15"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500"
                      />
                    </div>

                    {/* Universal Marketing */}
                    <div className="space-y-3 p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/30">
                      <h4 className="text-sm font-bold text-cyan-300 flex items-center gap-2">
                        <Palette className="w-4 h-4" />
                        Universal Marketing
                      </h4>
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_images_universal}
                        onChange={(e) => setEditForm({...editForm, max_images_universal: parseInt(e.target.value) || 0})}
                        placeholder="Max Photos"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-cyan-500"
                      />
                      <input
                        type="number"
                        min="0"
                        value={editForm.max_videos_universal}
                        onChange={(e) => setEditForm({...editForm, max_videos_universal: parseInt(e.target.value) || 0})}
                        placeholder="Max Vidéos"
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => saveQuotas(client.$id)}
                      className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                /* MODE AFFICHAGE */
                <div>
                  {/* Historique Total */}
                  <div className="mb-4 p-4 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-xl border border-indigo-500/30">
                    <p className="text-xs text-indigo-300 font-bold mb-2">📊 HISTORIQUE TOTAL</p>
                    <div className="text-2xl font-bold text-white">
                      {client.total_generations || 0} générations
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    {/* Virtual Try-On Stats */}
                    <div className="p-4 bg-pink-500/5 rounded-xl border border-pink-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Shirt className="w-5 h-5 text-pink-400" />
                        <p className="text-sm font-bold text-pink-300">VIRTUAL TRY-ON</p>
                      </div>
                      {hasVTryon ? (
                        <div className="space-y-2 text-sm text-white/80">
                          <div className="flex justify-between">
                            <span>📸 Photos:</span>
                            <span>{client.used_images_studiopro || 0} / {client.max_images_studiopro || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>✨ V12:</span>
                            <span>{client.used_videos_wan || 0} / {client.max_videos_wan || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>⚡ V15:</span>
                            <span>{client.used_videos_hailua || 0} / {client.max_videos_hailua || 0}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-white/40">Service non actif</p>
                      )}
                    </div>

                    {/* Universal Stats */}
                    <div className="p-4 bg-cyan-500/5 rounded-xl border border-cyan-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Palette className="w-5 h-5 text-cyan-400" />
                        <p className="text-sm font-bold text-cyan-300">UNIVERSAL MARKETING</p>
                      </div>
                      {hasUniversal ? (
                        <div className="space-y-2 text-sm text-white/80">
                          <div className="flex justify-between">
                            <span>📸 Photos:</span>
                            <span>{client.used_images_universal || 0} / {client.max_images_universal || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>🎬 Vidéos:</span>
                            <span>{client.used_videos_universal || 0} / {client.max_videos_universal || 0}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-white/40">Service non actif</p>
                      )}
                    </div>
                  </div>

                  {/* Boutons Reset */}
                  <div className="mb-4 p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-sm text-white/60 mb-3 font-bold">🔄 Réinitialiser</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <button
                        onClick={() => resetQuotas(client.$id, 'images_studiopro', 'Photos VTry-On')}
                        className="px-3 py-2 rounded-lg bg-pink-600/20 hover:bg-pink-600/40 text-pink-300 text-xs font-bold transition-all"
                      >
                        📸 Photos VT
                      </button>
                      <button
                        onClick={() => resetQuotas(client.$id, 'wan', 'Vidéos V12')}
                        className="px-3 py-2 rounded-lg bg-green-600/20 hover:bg-green-600/40 text-green-300 text-xs font-bold transition-all"
                      >
                        ✨ V12
                      </button>
                      <button
                        onClick={() => resetQuotas(client.$id, 'hailua', 'Vidéos V15')}
                        className="px-3 py-2 rounded-lg bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-300 text-xs font-bold transition-all"
                      >
                        ⚡ V15
                      </button>
                      <button
                        onClick={() => resetQuotas(client.$id, 'images_universal', 'Photos Universal')}
                        className="px-3 py-2 rounded-lg bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-300 text-xs font-bold transition-all"
                      >
                        📸 Photos Univ
                      </button>
                      <button
                        onClick={() => resetQuotas(client.$id, 'videos_universal', 'Vidéos Universal')}
                        className="px-3 py-2 rounded-lg bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 text-xs font-bold transition-all"
                      >
                        🎬 Vidéos Univ
                      </button>
                      <button
                        onClick={() => resetQuotas(client.$id, 'all', 'TOUT')}
                        className="px-3 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-300 text-xs font-bold transition-all"
                      >
                        🔥 TOUT
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => startEdit(client)}
                    className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
                  >
                    <Edit2 className="w-5 h-5" />
                    Modifier Quotas
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {filteredClients.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/40 text-lg">Aucune page trouvée</p>
          </div>
        )}
      </div>
    </div>
  );
}